import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';

export default class LoadingStateService extends Service {
  @tracked isGlobalLoading = false;
  @tracked currentLoadingRoute: string | null = null;
  @tracked loadingStartTime: number | null = null;
  @tracked loadingMessage = 'Loading...';
  @tracked loadingSubtitle: string | null = null;

  // Track specific loading states
  @tracked loadingStates = new Map<string, boolean>();

  @action
  startGlobalLoading(route: string, message?: string, subtitle?: string) {
    this.isGlobalLoading = true;
    this.currentLoadingRoute = route;
    this.loadingStartTime = Date.now();
    
    if (message) this.loadingMessage = message;
    if (subtitle) this.loadingSubtitle = subtitle;
  }

  @action
  stopGlobalLoading() {
    this.isGlobalLoading = false;
    this.currentLoadingRoute = null;
    this.loadingStartTime = null;
    this.loadingMessage = 'Loading...';
    this.loadingSubtitle = null;
  }

  @action
  setLoadingState(key: string, isLoading: boolean) {
    if (isLoading) {
      this.loadingStates.set(key, true);
    } else {
      this.loadingStates.delete(key);
    }
    // Trigger reactivity
    this.loadingStates = new Map(this.loadingStates);
  }

  @action
  isLoading(key: string): boolean {
    return this.loadingStates.get(key) ?? false;
  }

  @action
  clearAllLoadingStates() {
    this.loadingStates.clear();
    this.loadingStates = new Map();
  }

  get loadingDuration(): number | null {
    if (!this.loadingStartTime) return null;
    return Date.now() - this.loadingStartTime;
  }

  get hasAnyLoading(): boolean {
    return this.isGlobalLoading || this.loadingStates.size > 0;
  }

  // Utility methods for common loading patterns
  @action
  async withLoading<T>(key: string, asyncOperation: () => Promise<T>): Promise<T> {
    this.setLoadingState(key, true);
    try {
      const result = await asyncOperation();
      return result;
    } finally {
      this.setLoadingState(key, false);
    }
  }
}